import * as React from 'react'
import { ReactElement } from 'react'
import { hydrate, render } from 'react-dom'
import * as ReactDOMServer from 'react-dom/server'
import { ServerStyleSheet } from 'styled-components'
import { getStyles } from 'typestyle'
import { getGitCommit } from '../config/config'
import { Source } from './sources'

/**
 * A widget is a small React-rendered App served by lambda.
 *
 * The widget gets requested through our API (e.g. https://dynasty.com/api/nps_survey) and lambda
 * serves pre-rendered (server-side) HTML.
 *
 * Once the HTML is served to the client, the client requests the lambda JS bundle which
 * "hydrates" the rendered HTML (calls React lifecycle hooks, client-side JavaScript, etc)
 *
 * This wrapper function provides both methods to render the initial HTML document for render props
 * as well as an initializer for client-side hydration.
 */
export function createWidget<T>(id: string, node: (props: T) => ReactElement<T>, backgroundColor?: string) {
  return {
    renderHtml: (title: string, props: T, hydrateProps?: T): string => {
      const sheet = new ServerStyleSheet()
      try {
        const body = ReactDOMServer.renderToString(sheet.collectStyles(node(props)))
        return ReactDOMServer.renderToString(
          <html>
            <head>
              <title>{title}</title>
              {hydrateProps && (
                <script
                  dangerouslySetInnerHTML={{
                    __html: `window.dynasty = { "isProduction": ${
                      process.env.NODE_ENV === 'production'
                    }, "commitHash": "${getGitCommit()}", "hydrateProps": ${JSON.stringify(hydrateProps)} }`,
                  }}
                />
              )}
              {sheet.getStyleElement()}
              <style
                dangerouslySetInnerHTML={{
                  __html: getStyles(),
                }}
              />
              <meta name="viewport" content="width=device-width, initial-scale=1"></meta>
              <link href="/leasing/css/fonts.css" rel="stylesheet"></link>
              {/* for jQuery datepicker */}
              {id === 'agent-calendar-page' && (
                <>
                  <link rel="stylesheet" href={Source.JQUERY_CSS} />
                  <script src={Source.JQUERY}></script>
                  <script src={Source.JQUERY_UI}></script>
                  <link href={Source.DATEPICKER_CSS} rel="stylesheet"></link>
                </>
              )}
            </head>
            <body
              style={{
                backgroundColor: backgroundColor || '#f5f5f6',
                fontSize: '14px',
                margin: '0px',
                fontFamily:
                  "-apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Helvetica', 'Arial', 'Segoe UI', 'Roboto', 'Oxygen-Sans', 'Ubuntu', 'Cantarell', sans-serif",
              }}
            >
              <div id={id} dangerouslySetInnerHTML={{ __html: body }} />
              <div id="calendar-modal" />
              <script src={`/leasing/bundle-lambda.js?nonce=${Math.random()}`} />
            </body>
          </html>
        )
      } catch (error) {
        console.log(error)
        return (error as Error).toString()
      } finally {
        sheet.seal()
      }
    },
    init: (props: T, renderNotHydrate?: boolean) => {
      const container = document.getElementById(id)
      if (container != null) {
        const fn = renderNotHydrate ? render : hydrate
        fn(node(props), container)
      }
    },
  }
}
